/**
 * Narrow localStorage helpers, namespaced to avoid collisions with anything
 * else sharing the origin. Everything JSON-encoded; missing keys, malformed
 * JSON, and disabled storage all collapse to the fallback.
 *
 * Rationale: the Chrome extension iframe is torn down every time the side
 * panel closes and reloaded fresh on reopen — router state and sessionStorage
 * do not survive that cycle, so we write durable state to localStorage.
 *
 * PHI constraint: only non-PHI structural values (route pathnames, UI flags)
 * may be persisted here. Search filters, member details, and anything carrying
 * HIPAA identifiers stay in memory only — see MemberSearchPage for the
 * in-memory restore pattern used when returning from a member profile.
 */

const NAMESPACE = "member-360:";

export const PersistKeys = {
  lastRoute: `${NAMESPACE}last-route`,
} as const;

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function getPersisted<T>(key: string, fallback: T): T {
  const ls = storage();
  if (!ls) return fallback;
  try {
    const raw = ls.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function setPersisted<T>(key: string, value: T): void {
  const ls = storage();
  if (!ls) return;
  try {
    ls.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full / disabled — acceptable to silently drop; we're a
    // convenience, not a system of record.
  }
}

export function clearPersisted(key: string): void {
  const ls = storage();
  if (!ls) return;
  try {
    ls.removeItem(key);
  } catch {
    /* noop */
  }
}

/**
 * Remove legacy persisted keys that previously held PHI. Runs once on app
 * boot to purge any data already sitting in existing users' localStorage
 * from earlier builds that persisted search filters / router state blobs.
 */
const LEGACY_PHI_KEYS = [`${NAMESPACE}search-filters`];

export function purgeLegacyPHI(): void {
  const ls = storage();
  if (!ls) return;
  for (const key of LEGACY_PHI_KEYS) {
    try {
      ls.removeItem(key);
    } catch {
      /* noop */
    }
  }
  // Rewrite lastRoute without its state payload, which previously carried
  // search filters when the user navigated from results to a profile.
  try {
    const raw = ls.getItem(PersistKeys.lastRoute);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { pathname?: unknown; state?: unknown };
    if (parsed && typeof parsed.pathname === "string" && "state" in parsed) {
      ls.setItem(
        PersistKeys.lastRoute,
        JSON.stringify({ pathname: parsed.pathname }),
      );
    }
  } catch {
    ls.removeItem(PersistKeys.lastRoute);
  }
}
